import { BusinessRuleErrorEnum } from "./business-rule-error.enum";
import type { BusinessRuleErrorEnum as BusinessRuleErrorEnumType } from "./business-rule-error.enum";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useModalHubStore } from "~/components/modal-hub";
import { useToast } from "@chatfood/design-system";

export type IBusinessRulesActionMapped = "updateItemQuantity" | "checkout";

export const useBusinessRulesValidation = () => {
  const basketStore = useBasketStore();
  const modalHub = useModalHubStore();

  const { t } = useLocale();
  const { toastUnexpectedError } = useToast();

  const removeDiscountAndItem = async (lineId: string) => {
    modalHub.setWarningConfirmLoading();

    try {
      await basketStore.removeDiscountAndItem(lineId);
    } catch {
      toastUnexpectedError();
    } finally {
      modalHub.hideWarning();
    }
  };

  const resolveBasketException = (
    action: IBusinessRulesActionMapped,
    error: unknown,
  ) => {
    const err = error as {
      data?: { item_id?: string; fields?: Record<string, unknown> };
      statusCode?: number;
      statusMessage?: string;
      message?: string;
    };

    if (err.statusCode !== 422) {
      modalHub.hideLoading();
      return { handled: false };
    }

    const itemName =
      basketStore.items.find((item) => item.itemId === err?.data?.item_id)
        ?.name ?? "";

    const mapErrors: Record<BusinessRuleErrorEnumType | string, string> = {
      gift_card_could_not_be_applied: t(
        "composables.use_business_rules_validation.resolve_basket_exception.gift_card_could_not_be_applied",
      ),
      store_not_accepting_orders: t(
        "composables.use_business_rules_validation.resolve_basket_exception.store_not_accepting_orders",
      ),
      item_not_found: t(
        "composables.use_business_rules_validation.resolve_basket_exception.item_not_found",
        { itemName },
      ),
      item_out_of_stock: t(
        "composables.use_business_rules_validation.resolve_basket_exception.item_out_of_stock",
        { itemName },
      ),
      loyalty_free_item_not_found: t(
        "composables.use_business_rules_validation.resolve_basket_exception.loyalty_free_item_not_found",
      ),
      maximum_order_capacity: t(
        "composables.use_business_rules_validation.resolve_basket_exception.maximum_order_capacity",
      ),
      loyalty_program_no_longer_available:
        action === "updateItemQuantity"
          ? t(
              "composables.use_business_rules_validation.resolve_basket_exception.loyalty_program_no_longer_available_update_item",
            )
          : t(
              "composables.use_business_rules_validation.resolve_basket_exception.loyalty_program_no_longer_available",
            ),
      address_missing: t(
        "composables.use_business_rules_validation.resolve_basket_exception.address_missing",
      ),
      voucher_rules_violated: t(
        "composables.use_business_rules_validation.resolve_basket_exception.voucher_rules_violated",
      ),
      discount_eligible_items_missing: t(
        "composables.use_business_rules_validation.resolve_basket_exception.discount_eligible_items_missing",
      ),
      basket_min_value: t(
        "composables.use_business_rules_validation.resolve_basket_exception.basket_min_value",
      ),
      customer_address_must_be_provided: t(
        "composables.use_business_rules_validation.resolve_basket_exception.customer_address_must_be_provided",
      ),
    };

    const errorFields = err?.data?.fields;

    if (errorFields) {
      const fieldErrors: Record<string, string> = {
        scheduledTo: t(
          "composables.use_business_rules_validation.resolve_checkout_exception.scheduled_to_past",
        ),
        "basket.discount.id": t(
          "composables.use_business_rules_validation.resolve_checkout_exception.invalid_discount_id",
        ),
      };

      const mappedFieldErrorMessage = Object.keys(errorFields)
        .filter((key) => Boolean(errorFields[key]))
        .map((key) => fieldErrors[key])
        .filter(Boolean)
        .join("\n");

      if (err.statusCode === 422 && mappedFieldErrorMessage) {
        modalHub.hideLoading();
        modalHub.showError(
          t(
            "composables.use_business_rules_validation.resolve_checkout_exception.modal_title",
          ),
          mappedFieldErrorMessage,
          t(
            "composables.use_business_rules_validation.resolve_checkout_exception.modal_cta_label",
          ),
        );
        return { handled: true, type: "field_validation_error" };
      }

      return { handled: false };
    }

    const mappedErrorMessage =
      mapErrors[err?.statusMessage ?? ""] ?? err?.message;

    if (err?.statusCode === 422) {
      if (
        err.statusMessage ===
          BusinessRuleErrorEnum.DISCOUNT_ELIGIBLE_ITEMS_MISSING &&
        err.data?.item_id
      ) {
        modalHub.hideLoading();
        modalHub.showWarning({
          title: t(
            "composables.use_business_rules_validation.discount_eligible_items_missing_modal.title",
          ),
          message: t(
            "composables.use_business_rules_validation.discount_eligible_items_missing_modal.message",
          ),
          dismissLabel: t(
            "composables.use_business_rules_validation.discount_eligible_items_missing_modal.dismiss_label",
          ),
          confirmLabel: t(
            "composables.use_business_rules_validation.discount_eligible_items_missing_modal.confirm_label",
          ),
          onWarningConfirm: () => removeDiscountAndItem(err.data!.item_id!),
        });
        return {
          handled: true,
          type: "discount_eligible_items_missing",
          lineId: err.data.item_id,
        };
      } else if (mappedErrorMessage) {
        modalHub.hideLoading();
        modalHub.showError(
          t(
            "composables.use_business_rules_validation.resolve_basket_exception.modal_title",
          ),
          mappedErrorMessage,
          t(
            "composables.use_business_rules_validation.resolve_basket_exception.modal_cta_label",
          ),
        );
        return { handled: true, type: "business_rule_error" };
      }

      modalHub.hideLoading();
      return { handled: false };
    }

    return { handled: false };
  };

  return { resolveBasketException };
};
